import { type FC, useEffect, useMemo, useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import Spinner from "~/common/components/Spinner/Spinner";
import TVAccountSelectionResultsTable from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/TVAccountSelectionResultsTable";
import {
  type AccountInfoData,
  getAccountsData,
} from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { sendData } from "~/domains/user/api/SetAccount";
import { invalidateNavigationCache } from "~/features/navigation/redux/loadNavigationEpic";
import { useTranslation } from "react-i18next";
import AccountSelectionSearch from "~/features/b2bAccountSelection/components/AccountSelectionView/components/AccountSelectionSearch";
import { Box, Button, styled } from "~/common/mui";

const StyledButton = styled(Button)(() => ({
  borderRadius: "100px !important",
  padding: "10px 100px !important",
}));
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { AccountSelectionBottomButton } from "~/pages/AccountSelectionPage/AccountSelectionPage";
import { Alert } from "~/common/mui/Alert/Alert";

export interface TVAccountSelectionrPageFilterConfig {
  query: string;
  queryType: string;
}

export const TVAccountSelectionPage: FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AccountInfoData[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountInfoData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [inputValue, setInputValue] = useState<string>("");

  const fetchData = async () => {
    try {
      const { rows, selectedAccountId } = await getAccountsData();
      const translatedRows = rows.map((row) => ({ ...row, country: t(row.country) }));
      setRows(translatedRows);
      setSelectedAccount(translatedRows.find((row) => row.id === selectedAccountId));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [t]);

  const onRadioClickChangeActiveState = (item: AccountInfoData) => {
    setSelectedAccount(item);
  };

  const filteredRows = useMemo(
    () =>
      rows.filter(({ name, number, endCustomerName, endCustomerCode, region, country, endCustomerSector }) =>
        [name, number, endCustomerName, endCustomerCode, region, country, endCustomerSector].some((value) =>
          value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      ),
    [rows, inputValue],
  );

  const isDesktop = useIsMinDesktopSize();
  const buttonDisabled = filteredRows.findIndex((account) => account === selectedAccount) === -1 || !selectedAccount;

  const handleAccountChange = () =>
    setAccount(selectedAccount!.uid, selectedAccount!.salesOrg, selectedAccount!.endCustomerCode);

  return (
    <>
      <Box sx={{ overflow: "auto" }}>
        <Box sx={{ position: "sticky", left: "0" }}>
          <PageTitle title={t("accountSelectionPage.tableView.header")} />

          {loading && !selectedAccount && <Spinner className="mb-30" />}

          <div className="d-flex flex-justify-between">
            <AccountSelectionSearch value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
            {isDesktop && filteredRows.length !== 0 && (
              <StyledButton className={"mb-3"} onClick={handleAccountChange} disabled={buttonDisabled}>
                {t("accountSelection.buttonText")}
              </StyledButton>
            )}
          </div>
        </Box>

        {!loading && !error && selectedAccount && (
          <TVAccountSelectionResultsTable
            rows={filteredRows}
            selectedId={selectedAccount.id}
            onRadioClickChangeActiveState={onRadioClickChangeActiveState}
          />
        )}

        {!loading && error && <Alert severity="error">{t("page.accountSelectionPage.error")}</Alert>}
        {isDesktop && filteredRows.length !== 0 && (
          <StyledButton sx={{ marginTop: "32px !important" }} onClick={handleAccountChange} disabled={buttonDisabled}>
            {t("accountSelection.buttonText")}
          </StyledButton>
        )}
      </Box>
      {!isDesktop && <AccountSelectionBottomButton onClick={handleAccountChange} disabled={buttonDisabled} />}
    </>
  );
};

const setAccount = (accountId: string, salesOrg?: string, endCustomerCode?: string) =>
  sendData({ accountId, salesOrg, endCustomerCode })
    .then((response) => response.json())
    .then((body) => {
      window.sessionStorage.setItem("accountChanged", "true");
      invalidateNavigationCache();
      redirectToUrl(body);
    });
